/**
 * Copyright 2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useApolloClient } from "@apollo/client";
import {
  GetEventsViewDocument,
  GetEventsViewQuery,
  useEventStatusChangedSubscription,
  useGetEventViewLazyQuery,
} from "~/generated/graphql";
import { isFinishedEventStatus } from "~/views/events/helpers";

type EventsViewEvent = NonNullable<
  GetEventsViewQuery["eventsView"]["result"]
>[number];

type Props = {
  event: EventsViewEvent;
};

export function useEventItemDataSync({ event }: Props) {
  const client = useApolloClient();

  const [getEventData, { data }] = useGetEventViewLazyQuery({
    variables: { id: event.id },
    fetchPolicy: "network-only",
  });

  const { data: eventStatusData } = useEventStatusChangedSubscription({
    variables: {
      input: {
        id: event.id,
      },
    },
    onData: () => {
      getEventData();
      client.refetchQueries({
        include: [GetEventsViewDocument],
      });
    },
  });

  const eventSource = data?.eventView ?? event;
  const liveEventStatus =
    eventStatusData?.eventStatusChanged ?? eventSource?.status;
  const isEventFinished = isFinishedEventStatus(liveEventStatus);

  return {
    getEventData,
    eventSource,
    liveEventStatus,
    isEventFinished,
  } as const;
}
